import { APP_URL } from "@/lib/cta";
import { capture, EVENTS, type AnalyticsEvent } from ".";

/**
 * App paths worth counting as conversions, mapped to their event name.
 * Keys are matched against `URL.pathname` of the clicked link.
 */
const TRACKED_APP_PATHS: Record<string, AnalyticsEvent> = {
  "/sign-in": EVENTS.SIGN_IN_CLICKED,
  "/sign-up": EVENTS.SIGN_UP_CLICKED,
};

/**
 * Sign-in/sign-up CTAs are plain `<a href>` elements scattered across every
 * landing page and route (see `signUpUrl()` call sites). Rather than wire an
 * `onClick` into ~40 anchors — and silently miss the next one someone adds —
 * we delegate off `document`, matching on the resolved destination URL.
 *
 * Returns a cleanup function that removes the listener.
 */
export function attachCtaTracking(): () => void {
  const appOrigin = new URL(APP_URL).origin;

  function onClick(e: MouseEvent): void {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest("a[href]");
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    if (!href) return;

    // Relative hrefs resolve against the current page; app CTAs are absolute.
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (url.origin !== appOrigin) return;

    const event = TRACKED_APP_PATHS[url.pathname];
    if (!event) return;

    capture(
      event,
      {
        page_path: window.location.pathname,
        // `signUpUrl()`/`signInUrl()` tag the originating CTA as `lp`.
        cta_source: url.searchParams.get("lp") ?? undefined,
        destination: url.pathname,
      },
      // The click navigates cross-origin immediately; a normal XHR would be
      // cancelled on unload before it reaches the ingestion endpoint.
      { transport: "sendBeacon" },
    );
  }

  document.addEventListener("click", onClick, { capture: true });

  return function cleanup(): void {
    document.removeEventListener("click", onClick, { capture: true });
  };
}
